/**
 * ApexProbeTile — single critical-probe display tile.
 *
 * Layout:
 *   ┌────────────────────────────────────────┐
 *   │ TMP                              ● OK  │   <- probe id + status badge
 *   │ 78.42°F                                │   <- big numeric value
 *   │ ╱╲╱╲╱╲╲╱╲╱   tank-temp                 │   <- sparkline + real name
 *   └────────────────────────────────────────┘
 *
 * Renders defensively:
 *   - `unit` may be `null` (some Trident firmwares omit it) — we then
 *     render the number bare, leaving a small typographic gap where the
 *     unit would have been. Never prints "null"/"undefined".
 *   - History may be empty on first render; the sparkline collapses to
 *     an empty SVG (no flat line at y=0).
 *
 * Color mapping:
 *   ok    → emerald badge (Badge variant "ok")
 *   warn  → amber badge   ("warn")
 *   danger→ coral badge   ("danger")
 *
 * The probe's *canonical* id (TMP, PH, ORP, ALK, CA, MG) is used as the
 * tile label since names like "Tmp"/"pH" are inconsistent across Apex
 * configurations. The real Apex display name is shown small underneath
 * the sparkline so the operator can still tie the tile back to the
 * source probe at a glance.
 */
import { Badge, type BadgeVariant } from './Badge';
import { Card, CardBody } from './Card';
import { Sparkline } from './Sparkline';
import {
  PROBE_BANDS,
  PROBE_LABELS,
  classifyProbe,
  type ProbeId,
  type ProbeStatus,
} from '../lib/probe-thresholds';
import type { ApexProbeSnapshot } from '../lib/types';

export interface ApexProbeTileProps {
  id: ProbeId;
  probe: ApexProbeSnapshot;
  /** Most-recent-last sample buffer for the sparkline. Optional. */
  history?: readonly number[];
}

const STATUS_VARIANT: Record<ProbeStatus, BadgeVariant> = {
  ok: 'ok',
  warn: 'warn',
  danger: 'danger',
};

const STATUS_LABEL: Record<ProbeStatus, string> = {
  ok: 'ok',
  warn: 'watch',
  danger: 'alert',
};

const STATUS_STROKE: Record<ProbeStatus, string> = {
  // Tailwind-equivalent colour values resolved at runtime, kept inline so the
  // Sparkline doesn't need to know about Tailwind itself.
  ok: '#34d399', // emerald-400
  warn: '#fbbf24', // amber-400
  danger: '#fb7185', // coral / rose-400
};

function formatProbeValue(value: number, id: ProbeId): string {
  if (!Number.isFinite(value)) return '—';
  // pH / Alk render to 2dp; everything else to 1dp. ORP / Ca / Mg are
  // typically integers but a stray decimal is fine.
  const decimals = id === 'pH' || id === 'alk' ? 2 : 1;
  return value.toFixed(decimals);
}

function formatUnit(probe: ApexProbeSnapshot, id: ProbeId): string {
  // Prefer the canonical unit label so all temperatures render `°F` even
  // when the Apex returns `"F"`. If we don't have a canonical label,
  // fall back to whatever the Apex sent (string), and finally to `''`.
  const canonical = PROBE_BANDS[id].unitLabel;
  if (canonical) return canonical;
  return typeof probe.unit === 'string' ? probe.unit : '';
}

export function ApexProbeTile({ id, probe, history = [] }: ApexProbeTileProps) {
  const status = classifyProbe(id, probe.value);
  const variant = STATUS_VARIANT[status];
  const value = formatProbeValue(probe.value, id);
  const unit = formatUnit(probe, id);

  return (
    <Card data-testid={`apex-probe-tile-${id}`} data-status={status}>
      <CardBody className="pt-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-reef-300">
            {id === 'pH' ? 'pH' : id.toUpperCase()}
          </span>
          <Badge variant={variant}>{STATUS_LABEL[status]}</Badge>
        </div>
        <div className="mt-2 flex items-baseline gap-1 tabular-nums">
          <span className="text-3xl font-semibold text-reef-50">{value}</span>
          {/* Always reserve the gap before the unit so missing units don't
              collapse the layout; the `&nbsp;` keeps tile widths consistent. */}
          <span className="text-sm text-reef-300">{unit || '\u00A0'}</span>
        </div>
        <div className="mt-2 flex items-end justify-between gap-2">
          <Sparkline
            points={history}
            stroke={STATUS_STROKE[status]}
            className="text-reef-400"
            ariaLabel={`${PROBE_LABELS[id]} history (${history.length} samples)`}
          />
          <span className="text-[10px] uppercase tracking-wide text-reef-500" title={probe.name}>
            {probe.name}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}